import React, { useState, useEffect, useCallback } from 'react';
import jsPDF from 'jspdf';
import { supabase } from '../supabaseClient';
import { nombreSeguroStorage } from '../lib/nombre-storage';
import { useCompany } from '../CompanyContext';
import { useImpersonation } from '../ImpersonationContext';
import { HAZARD_TO_PERMIT_TYPE } from '../constants/hazardPermitMap';
import RegistroFirmasModal from '../components/firmas/RegistroFirmasModal';
import ProgramaAnualPanel from '../components/programas/ProgramaAnualPanel';
import { programasDisponibles } from '../lib/programas/prog-datos';
import RegistrosConservados from '../components/firmas/RegistrosConservados';
import { cargarFirmas, contenidoDeCapacitacion, firmasDisponibles, type DatosFirmas } from '../lib/firmas/firmas-datos';
import { estadoVisible, faltantesContenido, origenEditable } from '../lib/firmas/firmas-logica';
import { ETIQUETA_CLASE, ETIQUETA_EVALUACION, type ClaseCapacitacion, type ResultadoEvaluacion } from '../lib/firmas/firmas-tipos';

// ── Types ──────────────────────────────────────────────────────────────────────

type TrainingStatus = 'planificada' | 'realizada' | 'cancelada';
type TrainingType   = 'altura' | 'caliente' | 'confinado' | 'electrico' | 'quimico' | 'loto' | 'excavacion' | 'maquinaria' | 'vias' | 'general';

interface SSOTraining {
  id: string;
  company_id: string;
  title: string;
  topic: string | null;
  trainer_name: string | null;
  scheduled_date: string;
  duration_hours: number | null;
  training_type: TrainingType;
  validity_months: number;
  status: TrainingStatus;
  notes: string | null;
  created_at: string;
  updated_at: string;
  // Registro firmado (TH 6 y TH 9): tipo, material y forma de evaluación (columnas nuevas de la base).
  record_kind?: ClaseCapacitacion | null;
  material?: string | null;
  evaluation_method?: string | null;
}

interface WorkerOption { id: string; full_name: string; cedula: string | null; position_id: string | null; }

interface HazardCatalogItem { id: string; category: string; hazard_type: string; }

// ── Constants ──────────────────────────────────────────────────────────────────

const STATUS_LABELS: Record<TrainingStatus, string> = {
  planificada: 'Planificada',
  realizada:   'Realizada',
  cancelada:   'Cancelada',
};

const STATUS_COLORS: Record<TrainingStatus, { bg: string; color: string; border: string }> = {
  planificada: { bg: 'rgba(59,130,246,0.12)',  color: '#60a5fa', border: 'rgba(59,130,246,0.3)'  },
  realizada:   { bg: 'rgba(47,191,113,0.12)',  color: '#2FBF71', border: 'rgba(47,191,113,0.3)'  },
  cancelada:   { bg: 'rgba(100,116,139,0.12)', color: '#64748b', border: 'rgba(100,116,139,0.3)' },
};

const TRAINING_TYPE_LABELS: Record<TrainingType, string> = {
  altura:     'Trabajo en altura',
  caliente:   'Trabajo en caliente',
  confinado:  'Espacio confinado',
  electrico:  'Trabajo eléctrico',
  quimico:    'Químico / Sustancias Peligrosas',
  loto:       'Bloqueo y Etiquetado (LOTO)',
  excavacion: 'Excavación / Zanjas',
  maquinaria: 'Maquinaria Pesada',
  vias:       'Trabajo en Vías Públicas',
  general:    'General',
};

const TRAINING_TYPE_COLORS: Record<TrainingType, { bg: string; color: string; border: string }> = {
  altura:     { bg: 'rgba(59,130,246,0.12)',   color: '#60a5fa', border: 'rgba(59,130,246,0.3)'   },
  caliente:   { bg: 'rgba(239,68,68,0.12)',    color: '#ef4444', border: 'rgba(239,68,68,0.3)'    },
  confinado:  { bg: 'rgba(167,139,250,0.12)',  color: '#a78bfa', border: 'rgba(167,139,250,0.3)'  },
  electrico:  { bg: 'rgba(234,179,8,0.12)',    color: '#eab308', border: 'rgba(234,179,8,0.3)'    },
  quimico:    { bg: 'rgba(34,197,94,0.12)',    color: '#22c55e', border: 'rgba(34,197,94,0.3)'    },
  loto:       { bg: 'rgba(249,115,22,0.12)',   color: '#f97316', border: 'rgba(249,115,22,0.3)'   },
  excavacion: { bg: 'rgba(180,83,9,0.12)',     color: '#b45309', border: 'rgba(180,83,9,0.3)'     },
  maquinaria: { bg: 'rgba(100,116,139,0.12)',  color: '#64748b', border: 'rgba(100,116,139,0.3)'  },
  vias:       { bg: 'rgba(236,72,153,0.12)',   color: '#ec4899', border: 'rgba(236,72,153,0.3)'   },
  general:    { bg: 'rgba(100,116,139,0.12)',  color: '#94a3b8', border: 'rgba(100,116,139,0.3)'  },
};

const EMPTY_FORM = {
  title: '', topic: '', trainer_name: '', scheduled_date: '',
  duration_hours: '', status: 'planificada' as TrainingStatus,
  training_type: 'general' as TrainingType, validity_months: '12', notes: '',
  record_kind: 'capacitacion' as ClaseCapacitacion, material: '', evaluation_method: '',
};

// ── PDF de constancia de capacitación ───────────────────────────────────────────

function mensajeDeError(e: unknown): string {
  return (e as { message?: string } | null)?.message ?? String(e);
}

async function generateConstanciaPDF(training: SSOTraining, companyName: string, attendees: WorkerOption[], uploadedBy: string | null): Promise<void> {
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });

  const PW = 210;
  const M  = 20;
  const CW = PW - M * 2;
  let y = M;

  const txt = (s: string, fs: number, style: 'normal' | 'bold' | 'italic' = 'normal', align: 'left' | 'center' | 'right' = 'left') => {
    doc.setFont('helvetica', style);
    doc.setFontSize(fs);
    doc.setTextColor(0, 0, 0);
    const x = align === 'center' ? PW / 2 : align === 'right' ? PW - M : M;
    doc.text(s, x, y, { align });
  };

  const fechaStr = new Date(training.scheduled_date + 'T00:00:00').toLocaleDateString('es-EC', {
    day: 'numeric', month: 'long', year: 'numeric',
  });

  // ── Título ─────────────────────────────────────────────────────────────────
  txt('CONSTANCIA DE CAPACITACIÓN', 16, 'bold', 'center'); y += 14;
  doc.setDrawColor(47, 191, 113);
  doc.line(M + 25, y, PW - M - 25, y); y += 12;

  // ── Datos de la capacitación ─────────────────────────────────────────────────
  txt(companyName, 11, 'bold', 'center'); y += 14;

  const field = (label: string, value: string) => {
    txt(label, 10, 'bold');
    doc.setFont('helvetica', 'normal'); doc.setFontSize(10);
    doc.text(value, M + 32, y);
    y += 7;
  };

  field('Capacitación:', training.title);
  field('Tema:',         training.topic || '—');
  field('Capacitador:',  training.trainer_name || '—');
  field('Fecha:',        fechaStr);
  field('Duración:',     training.duration_hours != null ? `${training.duration_hours} horas` : '—');
  y += 7;

  // ── Lista de asistentes ──────────────────────────────────────────────────────
  txt('TRABAJADORES ASISTENTES', 11, 'bold'); y += 8;

  doc.setFillColor(15, 23, 42);
  doc.setTextColor(255, 255, 255);
  doc.rect(M, y - 5, CW, 8, 'F');
  doc.setFont('helvetica', 'bold'); doc.setFontSize(9);
  doc.text('Nombre completo', M + 3, y);
  doc.text('Cédula', M + CW - 40, y);
  y += 8;

  doc.setTextColor(0, 0, 0);
  if (attendees.length === 0) {
    doc.setFont('helvetica', 'italic'); doc.setFontSize(9);
    doc.text('No se registró asistencia para esta capacitación.', M + 3, y);
    y += 8;
  } else {
    attendees.forEach((w, i) => {
      if (y > 270) { doc.addPage(); y = M; }
      doc.setFillColor(i % 2 === 0 ? 248 : 241, i % 2 === 0 ? 250 : 245, i % 2 === 0 ? 252 : 249);
      doc.rect(M, y - 5, CW, 7, 'F');
      doc.setFont('helvetica', 'normal'); doc.setFontSize(9);
      doc.text(w.full_name, M + 3, y);
      doc.text(w.cedula || '—', M + CW - 40, y);
      y += 7;
    });
  }

  y += 16;
  if (y > 250) { doc.addPage(); y = M; }

  // ── Firma ────────────────────────────────────────────────────────────────────
  doc.setDrawColor(0, 0, 0);
  doc.line(M, y, M + 80, y); y += 6;
  doc.setFont('helvetica', 'normal'); doc.setFontSize(9);
  doc.text('Firma del Responsable de SSO', M, y);

  const now = new Date();
  doc.setFont('helvetica', 'italic'); doc.setFontSize(8);
  doc.setTextColor(120, 120, 120);
  doc.text(`Generado por StorSafe el ${now.toLocaleDateString('es-EC')}`, PW - M, y, { align: 'right' });

  const filename = `Constancia_${training.title.replace(/[^a-zA-Z0-9]/g, '_')}.pdf`;
  const blob = doc.output('blob');
  doc.save(filename);

  // La constancia ya se descargó; si falla el guardado en StorSafe se avisa sin bloquear.
  let avisoRegistro: string | null = null;
  try {
    const path = `${training.company_id}/trainings/${nombreSeguroStorage(`${training.id}.pdf`)}`;
    let upErr = (await supabase.storage.from('sso-documents')
      .upload(path, blob, { upsert: false, contentType: 'application/pdf' })).error;

    if (upErr) {
      const { error: updateErr } = await supabase.storage.from('sso-documents')
        .update(path, blob, { contentType: 'application/pdf' });
      if (updateErr) throw updateErr;
    }

    const { data: pub } = supabase.storage.from('sso-documents').getPublicUrl(path);
    const { data: updRows, error: updErr } = await supabase
      .from('sso_trainings')
      .update({ pdf_url: pub.publicUrl })
      .eq('id', training.id)
      .select('id');
    if (updErr) throw updErr;
    // Con RLS, un UPDATE sin política que lo permita no da error: afecta 0 filas.
    if (!updRows || updRows.length === 0) throw new Error('la capacitación no se pudo actualizar (sin permiso de edición)');

    try {
      const { data: existingDoc } = await supabase
        .from('sso_documents')
        .select('id')
        .eq('company_id', training.company_id)
        .in('file_url', [pub.publicUrl, path])
        .maybeSingle();

      if (!existingDoc) {
        const { error: docErr } = await supabase.from('sso_documents').insert({
          company_id:  training.company_id,
          category:    'certificado',
          title:       `Constancia de capacitación - ${training.title}`,
          description: 'Generado automáticamente desde Capacitaciones',
          file_url:    pub.publicUrl,
          file_name:   `${training.id}.pdf`,
          uploaded_by: uploadedBy,
          version:     1,
          is_current:  true,
          checklist_item_id: 'TH-09', // Registro de asistencia a capacitaciones (Anexo 1, GTH 9)
        });
        if (docErr) throw docErr;
      }
    } catch (docErr) {
      console.warn('No se pudo registrar el documento en sso_documents:', docErr);
      avisoRegistro = 'no se pudo registrar en Documentos SSO (' + mensajeDeError(docErr) + ')';
    }
  } catch (err) {
    console.warn('No se pudo persistir pdf_url en sso_trainings:', err);
    avisoRegistro = 'no se pudo guardar el PDF en StorSafe (' + mensajeDeError(err) + ')';
  }
  if (avisoRegistro) {
    alert(`La constancia se descargó, pero ${avisoRegistro}.\n\nNo aparecerá en Documentos SSO ni en el checklist (TH-09). Intente generarla de nuevo; si el problema sigue, avise a soporte.`);
  }
}

// ── Component ──────────────────────────────────────────────────────────────────

export default function TrainingsPage() {
  const { company, profile } = useCompany();
  const { impersonated } = useImpersonation();
  const displayCompany = impersonated ?? company;
  const companyId = displayCompany?.id ?? '';

  const canEdit   = profile?.role === 'admin' || profile?.role === 'manager' || !!impersonated;
  const canDelete = profile?.role === 'admin' || !!impersonated;

  const [trainings,    setTrainings]    = useState<SSOTraining[]>([]);
  const [workers,      setWorkers]      = useState<WorkerOption[]>([]);
  const [attendanceCounts, setAttendanceCounts] = useState<Record<string, number>>({});
  const [loading,      setLoading]      = useState(true);

  // Filters
  const [search,       setSearch]       = useState('');
  const [filterStatus, setFilterStatus] = useState<TrainingStatus | ''>('');
  const [filterYear,   setFilterYear]   = useState(String(new Date().getFullYear()));

  // Training modal (create / edit)
  const [editTarget,  setEditTarget]  = useState<SSOTraining | null | undefined>(undefined); // undefined = closed
  const [form,        setForm]        = useState(EMPTY_FORM);
  const [saving,      setSaving]      = useState(false);

  // Attendance modal
  const [attendTarget,   setAttendTarget]   = useState<SSOTraining | null>(null);
  const [attendance,     setAttendance]     = useState<Record<string, boolean>>({});
  const [attendLoading,  setAttendLoading]  = useState(false);
  const [attendSaving,   setAttendSaving]   = useState(false);
  const [evaluaciones,   setEvaluaciones]   = useState<Record<string, ResultadoEvaluacion | ''>>({});

  // Registros firmados por el trabajador (asistencia a inducciones y capacitaciones)
  const [firmasOk,     setFirmasOk]     = useState(false);
  // Programa anual de capacitación (TH 8): solo con las tablas de los programas en la base.
  const [programasOk,  setProgramasOk]  = useState(false);
  useEffect(() => { if (companyId) programasDisponibles(companyId).then(setProgramasOk); }, [companyId]);
  const [datosFirmas,  setDatosFirmas]  = useState<DatosFirmas>({ registros: [], firmas: [], itemsEpp: [] });
  const [firmasTarget, setFirmasTarget] = useState<SSOTraining | null>(null);

  // Sugerencia: puestos cuyos riesgos típicos requieren el training_type actual
  const [hazardCatalog,        setHazardCatalog]        = useState<HazardCatalogItem[]>([]);
  const [requiringPositionIds, setRequiringPositionIds] = useState<Set<string>>(new Set());

  // Delete
  const [deleteTarget, setDeleteTarget] = useState<SSOTraining | null>(null);
  const [deleteStep,   setDeleteStep]   = useState(1);
  const [deleting,     setDeleting]     = useState(false);

  // ── Load ────────────────────────────────────────────────────────────────────

  const loadData = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    const [{ data: tr }, { data: wk }, { data: att }] = await Promise.all([
      supabase.from('sso_trainings').select('*').eq('company_id', companyId).order('scheduled_date', { ascending: false }),
      supabase.from('workers').select('id, full_name, cedula, position_id').eq('company_id', companyId).eq('status', 'activo').order('full_name'),
      supabase.from('sso_training_attendance').select('training_id, worker_id, attended').eq('company_id', companyId).eq('attended', true),
    ]);
    setTrainings((tr ?? []) as SSOTraining[]);
    setWorkers((wk ?? []) as WorkerOption[]);

    // Count attendees per training
    const counts: Record<string, number> = {};
    (att ?? []).forEach((a: any) => {
      counts[a.training_id] = (counts[a.training_id] ?? 0) + 1;
    });
    setAttendanceCounts(counts);
    const conFirmas = await firmasDisponibles(companyId);
    setFirmasOk(conFirmas);
    if (conFirmas) setDatosFirmas(await cargarFirmas(companyId, 'asistencia').catch(() => ({ registros: [], firmas: [], itemsEpp: [] })));
    setLoading(false);
  }, [companyId]);

  const registroDe = (trainingId: string) => datosFirmas.registros.find(r => r.training_id === trainingId) ?? null;
  const firmasDe = (recordId: string | undefined) => (recordId ? datosFirmas.firmas.filter(f => f.record_id === recordId) : []);
  const editableTraining = (trainingId: string) => origenEditable(registroDe(trainingId));

  useEffect(() => { loadData(); }, [loadData]);

  useEffect(() => {
    supabase.from('iper_hazard_catalog').select('id, category, hazard_type')
      .then(({ data }) => setHazardCatalog((data ?? []) as HazardCatalogItem[]));
  }, []);

  // ── Metrics ─────────────────────────────────────────────────────────────────

  const thisYear = new Date().getFullYear();
  const yearTrainings = trainings.filter(t => new Date(t.scheduled_date).getFullYear() === thisYear);
  const realizadas = yearTrainings.filter(t => t.status === 'realizada');
  const totalHours = realizadas.reduce((s, t) => s + (t.duration_hours ?? 0), 0);
  const pctCapacitados = realizadas.length > 0
    ? Math.min(100, Math.round(
        (Object.keys(attendanceCounts).filter(tid => yearTrainings.some(t => t.id === tid)).length
          / realizadas.length) * 100
      ))
    : 0;

  // ── Filtered ────────────────────────────────────────────────────────────────

  const filtered = trainings.filter(t => {
    if (filterYear && String(new Date(t.scheduled_date).getFullYear()) !== filterYear) return false;
    if (filterStatus && t.status !== filterStatus) return false;
    if (search && !t.title.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const years = Array.from(new Set(trainings.map(t => new Date(t.scheduled_date).getFullYear()))).sort((a, b) => b - a);
  if (!years.includes(thisYear)) years.unshift(thisYear);

  // ── Training CRUD ───────────────────────────────────────────────────────────

  function openCreate() {
    setForm(EMPTY_FORM);
    setEditTarget(null);
  }

  function openEdit(t: SSOTraining) {
    setForm({
      title:          t.title,
      topic:          t.topic ?? '',
      trainer_name:   t.trainer_name ?? '',
      scheduled_date: t.scheduled_date,
      duration_hours: t.duration_hours != null ? String(t.duration_hours) : '',
      status:         t.status,
      training_type:  t.training_type ?? 'general',
      validity_months: t.validity_months != null ? String(t.validity_months) : '12',
      notes:          t.notes ?? '',
      record_kind:    t.record_kind ?? 'capacitacion',
      material:       t.material ?? '',
      evaluation_method: t.evaluation_method ?? '',
    });
    setEditTarget(t);
  }

  async function handleSave() {
    if (!form.title.trim())        { alert('El título es requerido.'); return; }
    if (!form.scheduled_date)      { alert('La fecha programada es requerida.'); return; }
    setSaving(true);

    const payload = {
      company_id:     companyId,
      title:          form.title.trim(),
      topic:          form.topic.trim() || null,
      trainer_name:   form.trainer_name.trim() || null,
      scheduled_date: form.scheduled_date,
      duration_hours: form.duration_hours ? parseFloat(form.duration_hours) : null,
      status:         form.status,
      training_type:  form.training_type,
      validity_months: form.validity_months ? parseInt(form.validity_months, 10) : 12,
      notes:          form.notes.trim() || null,
      ...(firmasOk ? {
        record_kind:       form.record_kind,
        material:          form.material.trim() || null,
        evaluation_method: form.evaluation_method.trim() || null,
      } : {}),
    };

    if (editTarget) {
      const { error } = await supabase.from('sso_trainings').update(payload).eq('id', editTarget.id);
      if (error) { alert('Error al guardar: ' + error.message); setSaving(false); return; }
    } else {
      const { error } = await supabase.from('sso_trainings').insert(payload);
      if (error) { alert('Error al crear capacitación: ' + error.message); setSaving(false); return; }
    }

    setSaving(false);
    setEditTarget(undefined);
    loadData();
  }

  async function handleDelete() {
    if (!deleteTarget) return;
    setDeleting(true);
    const { error } = await supabase.from('sso_trainings').delete().eq('id', deleteTarget.id);
    if (error) { alert('Error al eliminar: ' + error.message); setDeleting(false); return; }
    setDeleting(false);
    setDeleteTarget(null);
    setDeleteStep(1);
    loadData();
  }

  // ── Attendance ──────────────────────────────────────────────────────────────

  async function openAttendance(t: SSOTraining) {
    setAttendTarget(t);
    setAttendLoading(true);
    setRequiringPositionIds(new Set());
    const { data, error } = await supabase
      .from('sso_training_attendance')
      .select(firmasOk ? 'worker_id, attended, evaluation_result' : 'worker_id, attended')
      .eq('training_id', t.id);
    if (error) { alert('Error al cargar asistencia: ' + error.message); setAttendLoading(false); return; }
    const map: Record<string, boolean> = {};
    const evals: Record<string, ResultadoEvaluacion | ''> = {};
    (data ?? []).forEach((a: any) => { map[a.worker_id] = a.attended; evals[a.worker_id] = a.evaluation_result ?? ''; });
    setAttendance(map);
    setEvaluaciones(evals);
    setAttendLoading(false);

    if (t.training_type !== 'general') {
      const uniquePositionIds = Array.from(new Set(
        workers.map(w => w.position_id).filter((id): id is string => !!id)
      ));
      if (uniquePositionIds.length > 0) {
        const { data: hazardLinks } = await supabase
          .from('job_position_typical_hazards')
          .select('job_position_id, hazard_catalog_id')
          .in('job_position_id', uniquePositionIds);

        const requiring = new Set<string>();
        (hazardLinks ?? []).forEach((link: { job_position_id: string; hazard_catalog_id: string }) => {
          const item = hazardCatalog.find(c => c.id === link.hazard_catalog_id);
          if (!item) return;
          const mapped = item.category === 'quimico' ? 'quimico' : HAZARD_TO_PERMIT_TYPE[item.hazard_type];
          if (mapped === t.training_type) requiring.add(link.job_position_id);
        });
        setRequiringPositionIds(requiring);
      }
    }
  }

  async function handleSaveAttendance() {
    if (!attendTarget) return;
    setAttendSaving(true);

    const rows = workers.map(w => ({
      training_id: attendTarget.id,
      company_id:  companyId,
      worker_id:   w.id,
      attended:    attendance[w.id] ?? false,
      ...(firmasOk ? { evaluation_result: (attendance[w.id] && evaluaciones[w.id]) || null } : {}),
    }));

    const { error } = await supabase
      .from('sso_training_attendance')
      .upsert(rows, { onConflict: 'training_id,worker_id' });

    if (error) { alert('Error al guardar asistencia: ' + error.message); setAttendSaving(false); return; }

    setAttendSaving(false);
    setAttendTarget(null);
    loadData();
  }

  // ── Constancia PDF ───────────────────────────────────────────────────────────

  const [generatingPdfId, setGeneratingPdfId] = useState<string | null>(null);

  async function handleGenerateConstancia(t: SSOTraining) {
    setGeneratingPdfId(t.id);
    const { data, error } = await supabase
      .from('sso_training_attendance')
      .select('worker_id')
      .eq('training_id', t.id)
      .eq('attended', true);
    setGeneratingPdfId(null);
    if (error) { alert('Error al cargar asistentes: ' + error.message); return; }

    const attendedIds = new Set((data ?? []).map((a: any) => a.worker_id));
    const attendees = workers.filter(w => attendedIds.has(w.id));
    await generateConstanciaPDF(t, displayCompany?.name ?? 'StorSafe', attendees, profile?.id ?? null);
  }

  // ── Render ──────────────────────────────────────────────────────────────────

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h2 style={{ color: '#f1f5f9', fontSize: '1.25rem', fontWeight: 700, margin: 0 }}>Capacitaciones SSO</h2>
          <p style={{ color: '#64748b', fontSize: '0.82rem', margin: '4px 0 0' }}>
            {yearTrainings.length} capacitación{yearTrainings.length !== 1 ? 'es' : ''} registrada{yearTrainings.length !== 1 ? 's' : ''} en {thisYear}
          </p>
        </div>
        {canEdit && (
          <button onClick={openCreate} style={primaryBtn}>+ Nueva capacitación</button>
        )}
      </div>

      {/* Metrics */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12 }}>
        <MetricCard label={`Total ${thisYear}`}     value={String(yearTrainings.length)}                              color="#a78bfa" bg="rgba(167,139,250,0.1)" />
        <MetricCard label="Horas impartidas"      value={totalHours.toFixed(1) + ' h'}                              color="#2FBF71" bg="rgba(47,191,113,0.1)"  />
        <MetricCard label="Realizadas"            value={String(realizadas.length)}                                  color="#60a5fa" bg="rgba(59,130,246,0.1)" />
        <MetricCard label="Con asistencia reg."   value={pctCapacitados + '%'}                                       color="#fbbf24" bg="rgba(245,158,11,0.1)" />
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
        <input style={{ ...filterInp, flex: '1 1 180px', minWidth: 150 }} placeholder="Buscar por título…" value={search} onChange={e => setSearch(e.target.value)} />
        <select style={filterInp} value={filterYear} onChange={e => setFilterYear(e.target.value)}>
          {years.map(y => <option key={y} value={String(y)}>{y}</option>)}
        </select>
        <select style={filterInp} value={filterStatus} onChange={e => setFilterStatus(e.target.value as TrainingStatus | '')}>
          <option value="">Estado: todos</option>
          {(Object.keys(STATUS_LABELS) as TrainingStatus[]).map(s =>
            <option key={s} value={s}>{STATUS_LABELS[s]}</option>
          )}
        </select>
        {(search || filterStatus) && (
          <button onClick={() => { setSearch(''); setFilterStatus(''); }}
            style={{ background: 'none', border: 'none', color: '#64748b', cursor: 'pointer', fontSize: '0.82rem' }}>
            ✕ Limpiar
          </button>
        )}
      </div>

      {programasOk && (
        <ProgramaAnualPanel tipo="capacitacion" companyId={companyId} empresa={displayCompany?.name ?? ''} puedeEditar={canEdit}
          onCapacitacionCreada={loadData} />
      )}

      {/* Table */}
      <div style={{ background: '#1e293b', border: '1px solid #334155', borderRadius: 14, overflow: 'hidden' }}>
        {loading ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#475569', fontSize: '0.88rem' }}>Cargando…</div>
        ) : filtered.length === 0 ? (
          <div style={{ padding: 48, textAlign: 'center', color: '#475569', fontSize: '0.88rem' }}>
            {trainings.length === 0 ? 'Aún no hay capacitaciones registradas.' : 'Sin resultados para los filtros aplicados.'}
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.84rem' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid #334155' }}>
                  {['Capacitación', 'Capacitador', 'Fecha', 'Duración', 'Tipo', 'Estado', 'Asistentes', ''].map(h => (
                    <th key={h} style={thStyle}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map(t => {
                  const sc = STATUS_COLORS[t.status];
                  return (
                    <tr key={t.id}
                      style={{ borderBottom: '1px solid rgba(51,65,85,0.5)', transition: 'background 0.1s' }}
                      onMouseEnter={e => (e.currentTarget.style.background = 'rgba(255,255,255,0.02)')}
                      onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}>

                      <td style={td}>
                        <div style={{ color: '#e2e8f0', fontWeight: 600 }}>{t.title}</div>
                        {t.topic && <div style={{ color: '#64748b', fontSize: '0.74rem' }}>{t.topic}</div>}
                        {firmasOk && t.record_kind && t.record_kind !== 'capacitacion' && (
                          <div style={{ color: '#a78bfa', fontSize: '0.72rem', fontWeight: 600 }}>{ETIQUETA_CLASE[t.record_kind]}</div>
                        )}
                      </td>
                      <td style={{ ...td, color: '#94a3b8', fontSize: '0.82rem' }}>{t.trainer_name ?? '—'}</td>
                      <td style={{ ...td, color: '#cbd5e1', fontSize: '0.82rem', whiteSpace: 'nowrap' }}>
                        {new Date(t.scheduled_date + 'T00:00:00').toLocaleDateString('es-EC')}
                      </td>
                      <td style={{ ...td, color: '#64748b', fontSize: '0.82rem' }}>
                        {t.duration_hours != null ? `${t.duration_hours} h` : '—'}
                      </td>
                      <td style={td}>
                        {(() => {
                          const tc = TRAINING_TYPE_COLORS[t.training_type ?? 'general'];
                          return (
                            <span style={{ background: tc.bg, color: tc.color, border: `1px solid ${tc.border}`, borderRadius: 6, padding: '2px 8px', fontSize: '0.72rem', fontWeight: 700, whiteSpace: 'nowrap' }}>
                              {TRAINING_TYPE_LABELS[t.training_type ?? 'general']}
                            </span>
                          );
                        })()}
                      </td>
                      <td style={td}>
                        <span style={{ background: sc.bg, color: sc.color, border: `1px solid ${sc.border}`, borderRadius: 6, padding: '2px 8px', fontSize: '0.74rem', fontWeight: 700, whiteSpace: 'nowrap' }}>
                          {STATUS_LABELS[t.status]}
                        </span>
                      </td>
                      <td style={{ ...td, color: '#94a3b8', fontSize: '0.82rem', textAlign: 'center' }}>
                        {attendanceCounts[t.id] ?? 0}
                      </td>
                      <td style={{ ...td, textAlign: 'right', whiteSpace: 'nowrap' }}>
                        <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                          <button onClick={() => openAttendance(t)} style={ghostBtn}
                            onMouseEnter={e => { e.currentTarget.style.borderColor = '#2FBF71'; e.currentTarget.style.color = '#2FBF71'; }}
                            onMouseLeave={e => { e.currentTarget.style.borderColor = '#334155'; e.currentTarget.style.color = '#94a3b8'; }}>
                            Asistencia
                          </button>
                          {firmasOk && t.status === 'realizada' && (() => {
                            const reg = registroDe(t.id);
                            const est = estadoVisible(reg, firmasDe(reg?.id));
                            return (
                              <button onClick={() => setFirmasTarget(t)} style={{ ...ghostBtn, color: est.estado === 'firmado' ? '#2FBF71' : est.estado === 'en_firma' ? '#f59e0b' : ghostBtn.color }}
                                title="Registro de asistencia firmado por los trabajadores">
                                {est.estado === 'firmado' ? '✓ Firmado' : est.estado === 'en_firma' ? est.texto : 'Firmas'}
                              </button>
                            );
                          })()}
                          {t.status === 'realizada' && (
                            <button onClick={() => handleGenerateConstancia(t)} disabled={generatingPdfId === t.id} style={ghostBtn}
                              onMouseEnter={e => { e.currentTarget.style.borderColor = '#a78bfa'; e.currentTarget.style.color = '#a78bfa'; }}
                              onMouseLeave={e => { e.currentTarget.style.borderColor = '#334155'; e.currentTarget.style.color = '#94a3b8'; }}>
                              {generatingPdfId === t.id ? 'Generando…' : 'Generar constancia PDF'}
                            </button>
                          )}
                          {canEdit && (!firmasOk || editableTraining(t.id)) && (
                            <button onClick={() => openEdit(t)} style={ghostBtn}
                              onMouseEnter={e => { e.currentTarget.style.borderColor = '#60a5fa'; e.currentTarget.style.color = '#60a5fa'; }}
                              onMouseLeave={e => { e.currentTarget.style.borderColor = '#334155'; e.currentTarget.style.color = '#94a3b8'; }}>
                              Editar
                            </button>
                          )}
                          {canDelete && (
                            <button onClick={() => { setDeleteTarget(t); setDeleteStep(1); }} style={ghostBtn}
                              onMouseEnter={e => { e.currentTarget.style.borderColor = '#ef4444'; e.currentTarget.style.color = '#ef4444'; }}
                              onMouseLeave={e => { e.currentTarget.style.borderColor = '#334155'; e.currentTarget.style.color = '#94a3b8'; }}>
                              Eliminar
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {firmasOk && (
        <RegistrosConservados companyId={companyId} tipo="asistencia" usuarioId={profile?.id ?? null} puedeEditar={canEdit} esAdmin={canDelete} recarga={datosFirmas} />
      )}

      {filtered.length > 0 && (
        <p style={{ color: '#475569', fontSize: '0.78rem', textAlign: 'right', margin: 0 }}>
          {filtered.length} de {trainings.length} registro{trainings.length !== 1 ? 's' : ''}
        </p>
      )}

      {/* ── Training Modal (create / edit) ──────────────────────────────────── */}
      {editTarget !== undefined && (
        <div style={overlay} onClick={e => e.target === e.currentTarget && !saving && setEditTarget(undefined)}>
          <div style={modalBox}>
            <div style={modalHeader}>
              <h2 style={{ color: '#f1f5f9', fontSize: '1.05rem', fontWeight: 700, margin: 0 }}>
                {editTarget ? 'Editar capacitación' : 'Nueva capacitación'}
              </h2>
              <button onClick={() => !saving && setEditTarget(undefined)} style={closeBtn}>✕</button>
            </div>
            <div style={{ padding: '0 24px 24px', display: 'flex', flexDirection: 'column', gap: 14, marginTop: 20 }}>

              <Field label="Título *">
                <input style={inp} value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} placeholder="Ej: Uso correcto de EPP" />
              </Field>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <Field label="Tema">
                  <input style={inp} value={form.topic} onChange={e => setForm(f => ({ ...f, topic: e.target.value }))} placeholder="Ej: Seguridad química" />
                </Field>
                <Field label="Capacitador">
                  <input style={inp} value={form.trainer_name} onChange={e => setForm(f => ({ ...f, trainer_name: e.target.value }))} placeholder="Nombre del instructor" />
                </Field>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
                <Field label="Fecha programada *">
                  <input style={inp} type="date" value={form.scheduled_date} onChange={e => setForm(f => ({ ...f, scheduled_date: e.target.value }))} />
                </Field>
                <Field label="Duración (horas)">
                  <input style={inp} type="number" min={0} step={0.5} value={form.duration_hours} onChange={e => setForm(f => ({ ...f, duration_hours: e.target.value }))} placeholder="Ej: 2.5" />
                </Field>
                <Field label="Estado">
                  <select style={sel} value={form.status} onChange={e => setForm(f => ({ ...f, status: e.target.value as TrainingStatus }))}>
                    {(Object.keys(STATUS_LABELS) as TrainingStatus[]).map(s =>
                      <option key={s} value={s}>{STATUS_LABELS[s]}</option>
                    )}
                  </select>
                </Field>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 12 }}>
                <Field label="Tipo de capacitación">
                  <select style={sel} value={form.training_type} onChange={e => setForm(f => ({ ...f, training_type: e.target.value as TrainingType }))}>
                    {(Object.keys(TRAINING_TYPE_LABELS) as TrainingType[]).map(t =>
                      <option key={t} value={t}>{TRAINING_TYPE_LABELS[t]}</option>
                    )}
                  </select>
                </Field>
                <Field label="Vigencia (meses)">
                  <input style={inp} type="number" min={1} max={120} value={form.validity_months} onChange={e => setForm(f => ({ ...f, validity_months: e.target.value }))} placeholder="12" />
                </Field>
              </div>
              {firmasOk && (
                <>
                  <Field label="Registro (Anexo 1)">
                    <select style={sel} value={form.record_kind} onChange={e => setForm(f => ({ ...f, record_kind: e.target.value as ClaseCapacitacion }))}>
                      {(Object.keys(ETIQUETA_CLASE) as ClaseCapacitacion[]).map(k => <option key={k} value={k}>{ETIQUETA_CLASE[k]}</option>)}
                    </select>
                  </Field>
                  <Field label="Material entregado o usado">
                    <input style={inp} value={form.material} maxLength={1000} onChange={e => setForm(f => ({ ...f, material: e.target.value }))} placeholder="Ej: Presentación, folleto de EPP" />
                  </Field>
                  <Field label="Evaluación de conocimientos">
                    <input style={inp} value={form.evaluation_method} maxLength={1000} onChange={e => setForm(f => ({ ...f, evaluation_method: e.target.value }))} placeholder="Ej: Prueba escrita de 10 preguntas" />
                  </Field>
                </>
              )}
              <Field label="Notas">
                <textarea style={{ ...inp, minHeight: 72, resize: 'vertical' }} value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} placeholder="Observaciones adicionales…" />
              </Field>

            </div>
            <div style={modalFooter}>
              <button onClick={() => !saving && setEditTarget(undefined)} style={cancelBtn}>Cancelar</button>
              <button onClick={handleSave} disabled={saving} style={{ ...saveBtn, opacity: saving ? 0.7 : 1 }}>
                {saving ? 'Guardando…' : editTarget ? 'Guardar cambios' : 'Crear capacitación'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Attendance Modal ─────────────────────────────────────────────────── */}
      {attendTarget && (
        <div style={overlay} onClick={e => e.target === e.currentTarget && !attendSaving && setAttendTarget(null)}>
          <div style={{ ...modalBox, maxWidth: 520 }}>
            <div style={modalHeader}>
              <div>
                <h2 style={{ color: '#f1f5f9', fontSize: '1.05rem', fontWeight: 700, margin: 0 }}>Lista de asistencia</h2>
                <p style={{ color: '#64748b', fontSize: '0.78rem', margin: '4px 0 0' }}>{attendTarget.title}</p>
              </div>
              <button onClick={() => !attendSaving && setAttendTarget(null)} style={closeBtn}>✕</button>
            </div>

            <div style={{ padding: '16px 24px', maxHeight: 420, overflowY: 'auto' }}>
              {firmasOk && !editableTraining(attendTarget.id) && (
                <p style={{ color: '#f59e0b', fontSize: '0.8rem', margin: '0 0 10px' }}>
                  La lista está en firma o firmada: no se edita. Para cambiarla, vuelve a editar el registro o pide al administrador que lo reabra.
                </p>
              )}
              {attendLoading ? (
                <p style={{ color: '#475569', fontSize: '0.88rem', textAlign: 'center', padding: 24 }}>Cargando…</p>
              ) : workers.length === 0 ? (
                <p style={{ color: '#475569', fontSize: '0.88rem', textAlign: 'center', padding: 24 }}>No hay trabajadores activos registrados.</p>
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.84rem' }}>
                  <thead>
                    <tr style={{ borderBottom: '1px solid #334155' }}>
                      <th style={{ ...thStyle, paddingLeft: 0 }}>Trabajador</th>
                      <th style={{ ...thStyle, textAlign: 'center' }}>Asistió</th>
                      {firmasOk && <th style={{ ...thStyle, textAlign: 'center' }}>Evaluación</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {workers.map(w => (
                      <tr key={w.id} style={{ borderBottom: '1px solid rgba(51,65,85,0.4)' }}>
                        <td style={{ ...td, paddingLeft: 0 }}>
                          <div style={{ color: '#e2e8f0', fontWeight: 500, display: 'flex', alignItems: 'center', gap: 6 }}>
                            {w.full_name}
                            {w.position_id && requiringPositionIds.has(w.position_id) && (
                              <span title="Su puesto requiere esta capacitación" style={requiresBadgeStyle}>
                                ⚠️ Su puesto la requiere
                              </span>
                            )}
                          </div>
                          {w.cedula && <div style={{ color: '#475569', fontSize: '0.74rem' }}>{w.cedula}</div>}
                        </td>
                        <td style={{ ...td, textAlign: 'center' }}>
                          <input
                            type="checkbox"
                            checked={attendance[w.id] ?? false}
                            onChange={e => setAttendance(a => ({ ...a, [w.id]: e.target.checked }))}
                            disabled={!canEdit || (firmasOk && !editableTraining(attendTarget.id))}
                            style={{ width: 16, height: 16, accentColor: '#2FBF71', cursor: canEdit ? 'pointer' : 'default' }}
                          />
                        </td>
                        {firmasOk && (
                          <td style={{ ...td, textAlign: 'center' }}>
                            <select aria-label={`Evaluación de ${w.full_name}`} style={{ ...sel, padding: '4px 6px', fontSize: '0.76rem', width: 'auto' }}
                              value={evaluaciones[w.id] ?? ''} disabled={!canEdit || !attendance[w.id] || !editableTraining(attendTarget.id)}
                              onChange={e => setEvaluaciones(v => ({ ...v, [w.id]: e.target.value as ResultadoEvaluacion | '' }))}>
                              <option value="">—</option>
                              {(Object.keys(ETIQUETA_EVALUACION) as ResultadoEvaluacion[]).map(k => <option key={k} value={k}>{ETIQUETA_EVALUACION[k]}</option>)}
                            </select>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            <div style={{ ...modalFooter, justifyContent: 'space-between' }}>
              <span style={{ color: '#64748b', fontSize: '0.8rem' }}>
                {Object.values(attendance).filter(Boolean).length} de {workers.length} asistentes
              </span>
              <div style={{ display: 'flex', gap: 10 }}>
                <button onClick={() => !attendSaving && setAttendTarget(null)} style={cancelBtn}>Cerrar</button>
                {canEdit && (!firmasOk || editableTraining(attendTarget.id)) && (
                  <button onClick={handleSaveAttendance} disabled={attendSaving} style={{ ...saveBtn, opacity: attendSaving ? 0.7 : 1 }}>
                    {attendSaving ? 'Guardando…' : 'Guardar asistencia'}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Registro de asistencia firmado (TH 6 y TH 9) ── */}
      {firmasTarget && (() => {
        const reg = registroDe(firmasTarget.id);
        const t = firmasTarget;
        return (
          <RegistroFirmasModal
            titulo={`${t.title} · ${new Date(t.scheduled_date + 'T00:00:00').toLocaleDateString('es-EC')}`}
            companyId={companyId}
            usuarioId={profile?.id ?? null}
            registro={reg}
            firmas={firmasDe(reg?.id)}
            origen={{ training_id: t.id }}
            armarContenido={async () => {
              const contenido = await contenidoDeCapacitacion(companyId, t, displayCompany?.name ?? '');
              return { contenido, faltantes: faltantesContenido(contenido, { realizada: t.status === 'realizada' }) };
            }}
            puedeEditar={canEdit}
            esAdmin={canDelete}
            onCambio={loadData}
            onCerrar={() => setFirmasTarget(null)}
          />
        );
      })()}

      {/* ── Delete Modal ─────────────────────────────────────────────────────── */}
      {deleteTarget && (
        <div style={overlay} onClick={e => e.target === e.currentTarget && !deleting && setDeleteTarget(null)}>
          <div style={{ ...modalBox, maxWidth: 420 }}>
            <div style={modalHeader}>
              <h2 style={{ color: '#f1f5f9', fontSize: '1.05rem', fontWeight: 700, margin: 0 }}>Eliminar capacitación</h2>
              <button onClick={() => !deleting && setDeleteTarget(null)} style={closeBtn}>✕</button>
            </div>
            <div style={{ padding: '20px 24px' }}>
              {deleteStep === 1 ? (
                <>
                  <p style={{ color: '#cbd5e1', fontSize: '0.88rem', margin: '0 0 8px' }}>
                    ¿Eliminar <strong style={{ color: '#f1f5f9' }}>{deleteTarget.title}</strong>?
                  </p>
                  <p style={{ color: '#64748b', fontSize: '0.82rem', margin: '0 0 20px' }}>
                    Se eliminarán también todos los registros de asistencia de esta capacitación.
                  </p>
                  <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                    <button onClick={() => setDeleteTarget(null)} style={cancelBtn}>Cancelar</button>
                    <button onClick={() => setDeleteStep(2)} style={{ ...saveBtn, background: '#dc2626' }}>Continuar</button>
                  </div>
                </>
              ) : (
                <>
                  <p style={{ color: '#fca5a5', fontSize: '0.88rem', fontWeight: 600, margin: '0 0 20px' }}>
                    ⚠ Confirma que deseas eliminar definitivamente esta capacitación. Esta acción no se puede deshacer.
                  </p>
                  <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                    <button onClick={() => setDeleteStep(1)} style={cancelBtn}>Atrás</button>
                    <button onClick={handleDelete} disabled={deleting} style={{ ...saveBtn, background: '#dc2626', opacity: deleting ? 0.7 : 1 }}>
                      {deleting ? 'Eliminando…' : 'Eliminar definitivamente'}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Sub-components ─────────────────────────────────────────────────────────────

function MetricCard({ label, value, color, bg }: { label: string; value: string; color: string; bg: string }) {
  return (
    <div style={{ background: '#1e293b', border: '1px solid #334155', borderRadius: 12, padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: 6 }}>
      <span style={{ fontSize: '1.5rem', fontWeight: 800, color }}>{value}</span>
      <div style={{ height: 3, borderRadius: 2, background: bg, position: 'relative', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', inset: 0, background: color, opacity: 0.5 }} />
      </div>
      <span style={{ color: '#64748b', fontSize: '0.76rem', lineHeight: 1.3 }}>{label}</span>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
      <label style={{ color: '#94a3b8', fontSize: '0.78rem', fontWeight: 600 }}>{label}</label>
      {children}
    </div>
  );
}

// ── Styles ─────────────────────────────────────────────────────────────────────

const primaryBtn:  React.CSSProperties = { padding: '9px 20px', background: 'linear-gradient(135deg, #1D6FA3 0%, #2FBF71 100%)', border: 'none', color: '#fff', fontWeight: 700, fontSize: '0.88rem', borderRadius: 8, cursor: 'pointer' };
const filterInp:   React.CSSProperties = { padding: '8px 12px', background: 'rgba(255,255,255,0.04)', border: '1px solid #334155', borderRadius: 8, color: '#e2e8f0', fontSize: '0.84rem', outline: 'none' };
const thStyle:     React.CSSProperties = { padding: '11px 14px', textAlign: 'left', color: '#64748b', fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', whiteSpace: 'nowrap' };
const td:          React.CSSProperties = { padding: '11px 14px', verticalAlign: 'middle' };
const ghostBtn:    React.CSSProperties = { padding: '5px 12px', background: 'transparent', border: '1px solid #334155', color: '#94a3b8', borderRadius: 6, cursor: 'pointer', fontSize: '0.76rem', transition: 'all 0.15s' };
const overlay:     React.CSSProperties = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)', backdropFilter: 'blur(3px)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', zIndex: 400, padding: '20px 16px', overflowY: 'auto' };
const modalBox:    React.CSSProperties = { background: '#1e293b', border: '1px solid #334155', borderRadius: 16, width: '100%', maxWidth: 580, boxShadow: '0 24px 48px rgba(0,0,0,0.5)', margin: 'auto' };
const modalHeader: React.CSSProperties = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '20px 24px', borderBottom: '1px solid #334155' };
const modalFooter: React.CSSProperties = { padding: '16px 24px', borderTop: '1px solid #334155', display: 'flex', gap: 10, justifyContent: 'flex-end', alignItems: 'center' };
const inp:         React.CSSProperties = { width: '100%', padding: '9px 12px', background: 'rgba(255,255,255,0.04)', border: '1px solid #334155', borderRadius: 8, color: '#e2e8f0', fontSize: '0.86rem', outline: 'none', boxSizing: 'border-box' };
const sel:         React.CSSProperties = { ...inp, cursor: 'pointer' };
const closeBtn:    React.CSSProperties = { background: 'none', border: 'none', color: '#64748b', fontSize: '1.2rem', cursor: 'pointer', lineHeight: 1 };
const cancelBtn:   React.CSSProperties = { padding: '9px 20px', background: 'transparent', border: '1px solid #334155', color: '#94a3b8', borderRadius: 8, cursor: 'pointer', fontSize: '0.88rem', fontWeight: 600 };
const saveBtn:     React.CSSProperties = { padding: '9px 24px', background: 'linear-gradient(135deg, #1D6FA3 0%, #2FBF71 100%)', border: 'none', color: '#fff', borderRadius: 8, cursor: 'pointer', fontSize: '0.88rem', fontWeight: 700 };
const requiresBadgeStyle: React.CSSProperties = { color: '#fbbf24', fontSize: '0.7rem', fontWeight: 600, whiteSpace: 'nowrap' };
